import React, { Suspense, lazy, useEffect, useState } from "react";
import axios from "axios";
import StepShell from "./StepShell";
import { renderMarkup } from "./markup";
import { dollars } from "./loadFormat";
import { errorMessage } from "./apiError";

// react-pdf is heavy; most carriers never open the full agreement.
const AgreementReader = lazy(() => import("./AgreementReader"));

export default function AgreementStep({
  tokenUrl,
  alreadyAgreed = false,
  notice = "",
  next = "payment",
  onDone,
  onDecline,
}) {
  const [agreement, setAgreement] = useState(null);
  const [agreed, setAgreed] = useState(alreadyAgreed);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${tokenUrl}/agreement`)
      .then(({ data }) => !cancelled && setAgreement(data))
      .catch(
        (err) =>
          !cancelled &&
          setError(errorMessage(err, "We couldn't load the agreement.")),
      );
    return () => {
      cancelled = true;
    };
  }, [tokenUrl]);

  async function accept() {
    setError("");
    setBusy(true);
    try {
      await axios.post(`${tokenUrl}/agreement-acknowledgment`, {
        agreed: true,
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const fee = agreement?.this_load_fee_cents;
  const min = agreement?.fee_min_cents;
  const max = agreement?.fee_max_cents;
  const cents = (c) => dollars(Math.round(c / 100));
  // The same tier line the reservation link text sends.
  const tiers = agreement?.fee_tiers_text && `${agreement.fee_tiers_text}.`;

  // The agreement's name in the consent opens the reader; links stay links.
  const strongAs = (text, key) =>
    text === agreement.agreement_title ? (
      <a
        key={key}
        href={agreement.url}
        onClick={(e) => {
          // Inside the tile's label: read, don't tick.
          e.preventDefault();
          e.stopPropagation();
          setReading(true);
        }}
        className="font-[inherit] font-medium !text-indigo-700 underline underline-offset-2"
      >
        {text}
      </a>
    ) : undefined;

  return (
    <StepShell
      question="Pay only when we book"
      onDecline={onDecline}
      footnote="No charge today"
      action={{
        label: NEXT_LABELS[next],
        onClick: accept,
        disabled: !agreed || !agreement,
        busy,
        busyLabel: "Saving…",
      }}
    >
      {agreement && (
        <div className="flex flex-col gap-6 short:gap-[16px]">
          <ul className="m-0 flex list-none flex-col gap-[18px] p-0 short:gap-2.5">
            <Line mark="✓" title="No booking, no fee">
              We charge the fee only once a load books.
            </Line>
            {fee ? (
              <Line mark="$" title={`${cents(fee)} fee at this price`}>
                {tiers}
              </Line>
            ) : (
              <Line
                mark="$"
                title={`${cents(min)}–${cents(max)} fee, depending on the load`}
              >
                {tiers}
              </Line>
            )}
            <Line mark="✓" title="You approve every booking">
              We never book a load without your OK.
            </Line>
          </ul>

          {/* The whole consent sentence sits in the tile; the box is named
              by its lead and described by the rest. */}
          <label
            className={`grid cursor-pointer grid-cols-[24px_minmax(0,1fr)] items-start gap-[12px] rounded-xl border-2 px-[16px] py-[12px] short:py-2.5 transition-[background-color,border-color] duration-200 focus-within:ring-4 focus-within:ring-indigo-200 active:scale-[0.99] ${
              agreed
                ? "border-emerald-700 bg-emerald-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <input
              id="book-it-agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              aria-labelledby="book-it-agree-lead"
              aria-describedby="book-it-agree-terms"
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={`grid h-6 w-6 place-items-center rounded-md border-2 text-white transition-colors duration-200 ${
                agreed
                  ? "border-emerald-700 bg-emerald-700"
                  : "border-slate-300 bg-white"
              }`}
            >
              {agreed && (
                <svg
                  viewBox="0 0 20 20"
                  className="h-4 w-4 motion-safe:animate-bi-pop"
                >
                  <path
                    d="M5 10.5l3.2 3.2L15 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="16"
                    className="motion-safe:animate-[bi-draw_.3s_ease-out_both]"
                  />
                </svg>
              )}
            </span>
            <span>
              <span
                id="book-it-agree-lead"
                className="block font-[Barlow_Condensed] text-[17px] font-semibold leading-snug text-slate-900"
              >
                {agreement.acknowledgment_lead}
              </span>
              <span
                id="book-it-agree-terms"
                className="mt-1 block font-[Roboto] text-[14.5px] leading-[1.45] text-slate-600 short:text-[14px] [&_strong]:font-medium [&_strong]:text-slate-700"
              >
                {renderMarkup(
                  agreement.acknowledgment_detail,
                  "!text-indigo-700",
                  strongAs,
                )}
              </span>
            </span>
          </label>
        </div>
      )}

      {(error || notice) && (
        <p className="m-0 font-[Roboto] text-[15px] text-red-700">
          {error || notice}
        </p>
      )}

      {reading && agreement?.url && (
        <ReaderBoundary url={agreement.url} onClose={() => setReading(false)}>
          <Suspense fallback={null}>
            <AgreementReader
              url={agreement.url}
              onClose={() => setReading(false)}
            />
          </Suspense>
        </ReaderBoundary>
      )}
    </StepShell>
  );
}

const NEXT_LABELS = {
  payment: "Continue to payment",
  offer: "Continue to your offer",
  finish: "Finish",
};

function Line({ mark, title, children }) {
  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)] items-start gap-2.5">
      <span
        aria-hidden="true"
        className="grid h-7 w-7 place-items-center rounded-full bg-emerald-50 text-[15px] font-bold text-emerald-700"
      >
        {mark}
      </span>
      <span>
        <span className="block font-[Barlow_Condensed] text-[17px] font-semibold leading-snug text-slate-900 short:text-[16px]">
          {title}
        </span>
        {children && (
          <span className="block font-[Roboto] mt-1 text-[15px] leading-[1.45] text-slate-600 short:text-[14px]">
            {children}
          </span>
        )}
      </span>
    </li>
  );
}

// The reader is fetched on demand; on a dropped connection, link to the PDF
// instead of losing the page.
class ReaderBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <p className="m-0 font-[Roboto] text-[15px] text-slate-700">
        The agreement viewer didn't load.{" "}
        <a
          href={this.props.url}
          target="_blank"
          rel="noopener noreferrer"
          className="!text-indigo-700 underline"
        >
          Open the PDF
        </a>{" "}
        instead.
      </p>
    );
  }
}
